import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import AdminPlans from './AdminPlans';
import { Save, X, Video, Calendar, CreditCard, Settings } from 'lucide-react';
import { formatCycleDate, computeCycleEnd, resolveCycleEnd } from '../utils/cycleUtils';
import ImageSourceField from './admin/ImageSourceField';

const TABS = [
  { id: 'general', label: 'General', icon: Settings },
  { id: 'ciclos', label: 'Ciclos', icon: Calendar },
  { id: 'zoom', label: 'Zoom', icon: Video },
  { id: 'planes', label: 'Planes comerciales', icon: CreditCard },
];

export default function ProgramEditorTabs({ program, cycles, onClose, onSaved, gymId = null }) {
  const isGymScoped = gymId != null && gymId !== '';
  const [tab, setTab] = useState('general');
  const [form, setForm] = useState({
    name: program?.name || '',
    color: program?.color || '#10b981',
    active: program?.active !== false,
    active_cycle_id: Number(program?.active_cycle_id) || '',
    descripcion: program?.descripcion || '',
    image_url: program?.image_url || '',
    welcome_video_url: program?.welcome_video_url || '',
    zoom_email: program?.zoom_email || '',
    zoom_account_id: program?.zoom_account_id || '',
    zoom_fallback_link: program?.zoom_fallback_link || '',
    zoom_accounts: Array.isArray(program?.zoom_accounts) ? program.zoom_accounts : [],
  });
  const [zoomStatus, setZoomStatus] = useState([]);
  const [zoomApiEnabled, setZoomApiEnabled] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/programs/zoom-pool')
      .then((res) => {
        if (isGymScoped) {
          setZoomStatus(res.data?.data || []);
          setZoomApiEnabled(!!res.data?.zoom_api_enabled);
        } else {
          setZoomStatus([]);
          setZoomApiEnabled(false);
        }
      })
      .catch(() => {
        setZoomStatus([]);
        setZoomApiEnabled(false);
      });
  }, [isGymScoped]);

  useEffect(() => {
    if (!isGymScoped) {
      api.get('/programs/zoom-master')
        .then((res) => {
          setZoomStatus(res.data?.data || []);
          setZoomApiEnabled(!!res.data?.zoom_api_enabled);
        })
        .catch(() => setZoomStatus([]));
    }
  }, [isGymScoped]);

  const saveProgram = async (e) => {
    e?.preventDefault();
    setError('');
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        color: form.color,
        active: form.active,
        active_cycle_id: Number(form.active_cycle_id) || null,
        welcome_video_url: form.welcome_video_url?.trim() || null,
        zoom_email: form.zoom_email.trim() || undefined,
        zoom_account_id: form.zoom_account_id || undefined,
        zoom_fallback_link: form.zoom_fallback_link.trim() || undefined,
      };
      if (isGymScoped) {
        payload.descripcion = form.descripcion?.trim() || '';
        payload.image_url = form.image_url?.trim() || '';
      }
      if (form.zoom_accounts?.length) {
        payload.zoom_accounts = form.zoom_accounts.map((a) => ({
          id: a.id,
          label: a.label,
          email: a.email,
          activo: a.activo !== false,
          observaciones: a.observaciones || '',
        }));
      }
      await api.put(`/programs/${program.id}`, payload);
      setMessage('Programa guardado');
      onSaved?.();
    } catch (err) {
      setError(err?.response?.data?.error || 'Error guardando programa');
    } finally {
      setSaving(false);
    }
  };

  const updateZoomAccount = (idx, field, value) => {
    setForm((prev) => {
      const accounts = [...(prev.zoom_accounts || [])];
      accounts[idx] = { ...accounts[idx], [field]: value };
      return { ...prev, zoom_accounts: accounts };
    });
  };

  const addZoomAccount = () => {
    setForm((prev) => ({
      ...prev,
      zoom_accounts: [
        ...(prev.zoom_accounts || []),
        { id: `${program.id}_${Date.now()}`, label: 'Nueva cuenta', email: '', activo: true, observaciones: '' },
      ],
    }));
  };

  const programCycles = cycles.filter((c) =>
    Number(c.id) === Number(form.active_cycle_id)
    || cycles.some(() => true),
  );

  return (
    <div className="d28d-admin-surface bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden mb-6">
      <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-slate-200 bg-stone-50">
        <div>
          <h4 className="text-lg font-bold text-stone-900">Editar programa: {program.name}</h4>
          {isGymScoped ? null : (
            <p className="text-xs text-stone-500">ID: {program.id}</p>
          )}
        </div>
        <button type="button" className="btn-secondary inline-flex items-center gap-1" onClick={onClose}>
          <X className="w-4 h-4" /> Cerrar
        </button>
      </div>

      <div className="flex flex-wrap gap-1 p-2 border-b border-slate-100 bg-white notranslate" translate="no">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium ${
              tab === id ? 'bg-indigo-100 text-indigo-800' : 'text-stone-600 hover:bg-stone-100'
            }`}
            onClick={() => setTab(id)}
          >
            <Icon className="w-4 h-4" />
            <span translate="no">{label}</span>
          </button>
        ))}
      </div>

      {error && <div className="mx-4 mt-4 bg-red-50 border border-red-200 text-red-800 p-3 rounded-xl text-sm">{error}</div>}
      {message && <div className="mx-4 mt-4 bg-emerald-50 border border-emerald-200 text-emerald-800 p-3 rounded-xl text-sm">{message}</div>}

      <div className="p-5">
        {tab === 'general' && (
          <form onSubmit={saveProgram} className="ui-light-panel border border-slate-200 rounded-xl p-5 space-y-4 max-w-2xl">
            <div className="ui-field">
              <span className="ui-field__label">Nombre</span>
              <input className="ui-input mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </div>
            {isGymScoped ? (
              <>
                <div className="ui-field">
                  <span className="ui-field__label">Descripción</span>
                  <textarea
                    className="ui-input mt-1 min-h-[80px] w-full"
                    value={form.descripcion}
                    onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                  />
                </div>
                <ImageSourceField
                  label="Imagen del programa"
                  value={form.image_url}
                  onChange={(url) => setForm({ ...form, image_url: url })}
                  alt={form.name}
                />
              </>
            ) : null}
            <div className="grid grid-cols-2 gap-4">
              <div className="ui-field">
                <span className="ui-field__label">Color</span>
                <input type="color" className="h-10 w-full rounded border border-slate-300 mt-1" value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })} />
              </div>
              <div className="flex items-end">
                <label className="inline-flex items-center gap-2 text-sm text-stone-800">
                  <input type="checkbox" checked={!!form.active}
                    onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                  Programa activo
                </label>
              </div>
            </div>
            <div className="ui-field">
              <span className="ui-field__label">Ciclo activo</span>
              <select className="ui-input mt-1" value={form.active_cycle_id}
                onChange={(e) => setForm({ ...form, active_cycle_id: Number(e.target.value) })}>
                <option value="">— Sin asignar —</option>
                {cycles.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} — {formatCycleDate(c.startDate)}
                  </option>
                ))}
              </select>
            </div>
            <div className="ui-field">
              <span className="ui-field__label">Video de bienvenida (programa)</span>
              <input
                className="ui-input mt-1"
                type="url"
                value={form.welcome_video_url || ''}
                onChange={(e) => setForm({ ...form, welcome_video_url: e.target.value })}
                placeholder="https://youtube.com/... o Zoom share"
              />
              <p className="text-xs text-stone-500 mt-1">
                Lo ven los usuarios finales de este programa. Las sedes pueden sobreescribirlo en Editar sede.
                Si está vacío, se usa el video del ciclo (legado).
              </p>
            </div>
            <button type="submit" className="btn-primary inline-flex items-center gap-2" disabled={saving}>
              <Save className="w-4 h-4" /> {saving ? 'Guardando…' : 'Guardar general'}
            </button>
          </form>
        )}

        {tab === 'ciclos' && (
          <div className="space-y-4">
            <p className="text-sm text-stone-600">
              {isGymScoped
                ? 'Ciclo activo del programa. Los ciclos se crean en Maestros → Ciclos de tu sede.'
                : 'Ciclo activo del programa. Los ciclos globales se administran en la sección inferior de esta pantalla.'}
            </p>
            <div className="bg-stone-50 rounded-2xl p-4">
              <p className="text-xs font-bold text-stone-400 uppercase">Ciclo asignado</p>
              {form.active_cycle_id ? (() => {
                const c = cycles.find((x) => Number(x.id) === Number(form.active_cycle_id));
                return c ? (
                  <>
                    <p className="text-lg font-bold">{c.name}</p>
                    <p className="text-sm text-stone-500">
                      {formatCycleDate(c.startDate)} → {formatCycleDate(resolveCycleEnd(c))}
                    </p>
                  </>
                ) : <p className="text-stone-500">Ciclo no encontrado</p>;
              })() : <p className="text-stone-500">Sin ciclo asignado</p>}
            </div>
            <select className="input max-w-md" value={form.active_cycle_id}
              onChange={(e) => setForm({ ...form, active_cycle_id: Number(e.target.value) })}>
              {cycles.map((c) => (
                <option key={c.id} value={c.id}>{c.name} ({formatCycleDate(c.startDate)})</option>
              ))}
            </select>
            <button type="button" className="btn-primary" onClick={saveProgram}>Guardar ciclo activo</button>
            <div className="overflow-x-auto mt-4">
              <table className="min-w-full text-sm">
                <thead><tr className="text-left text-stone-500">
                  <th className="py-2">Ciclo</th><th>Inicio</th><th>Fin</th><th>Etiqueta</th>
                </tr></thead>
                <tbody>
                  {programCycles.map((c) => (
                    <tr key={c.id} className="border-t border-stone-100">
                      <td className="py-2 font-medium">{c.name}</td>
                      <td>{formatCycleDate(c.startDate)}</td>
                      <td>{formatCycleDate(computeCycleEnd(c.startDate))}</td>
                      <td>{c.label || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'zoom' && (
          <div className="space-y-4 max-w-2xl">
            <div className={`rounded-xl border p-3 text-sm ${zoomApiEnabled ? 'border-lime-300 bg-lime-50' : 'border-amber-200 bg-amber-50'}`}>
              <p className="font-semibold text-stone-800">
                API Zoom automática: {zoomApiEnabled ? 'activa ✓' : 'pendiente (usa PMI o configura ZOOM_S2S_* en servidor)'}
              </p>
            </div>
            <p className="text-xs text-stone-500">
              {isGymScoped
                ? 'Selecciona la cuenta Zoom maestra para este programa. Las contraseñas viven solo en backend/.env; aquí defines el correo host para generar enlaces al programar clases.'
                : 'Las contraseñas Zoom se configuran en variables de entorno del servidor. Aquí solo se administra el correo asociado al programa.'}
            </p>
            {isGymScoped ? (
              <div className="space-y-3">
                <label className="label">Cuenta Zoom del programa</label>
                <select
                  className="input"
                  value={form.zoom_account_id || ''}
                  onChange={(e) => {
                    const id = e.target.value;
                    const pool = zoomStatus.find((a) => a.id === id);
                    setForm({
                      ...form,
                      zoom_account_id: id,
                      zoom_email: pool?.email || form.zoom_email,
                    });
                  }}
                >
                  <option value="">Seleccionar cuenta…</option>
                  {zoomStatus.map((acc) => (
                    <option key={acc.id} value={acc.id}>
                      {acc.label} — {acc.email}
                      {acc.password_configured ? ' ✓' : ' (falta contraseña en servidor)'}
                    </option>
                  ))}
                </select>
                <label className="label">Correo Zoom (host)</label>
                <input
                  className="input"
                  value={form.zoom_email}
                  onChange={(e) => setForm({ ...form, zoom_email: e.target.value })}
                  placeholder="d28dzoom2@gmail.com"
                />
                {form.zoom_account_id ? (
                  <p className="text-xs text-stone-500">
                    Contraseña en servidor:{' '}
                    {zoomStatus.find((a) => a.id === form.zoom_account_id)?.password_configured
                      ? 'configurada ✓'
                      : 'falta variable D28D_ZOOM_PASSWORD_* en backend/.env'}
                  </p>
                ) : null}
                <label className="label">Enlace fijo de sala Zoom (PMI)</label>
                <input
                  className="input"
                  value={form.zoom_fallback_link}
                  onChange={(e) => setForm({ ...form, zoom_fallback_link: e.target.value })}
                  placeholder="https://zoom.us/j/12345678901"
                />
                <p className="text-xs text-stone-500">
                  Opcional. Se usa al generar Zoom si no hay API S2S configurada. Debe ser un enlace numérico de zoom.us/j/
                </p>
              </div>
            ) : !form.zoom_accounts?.length ? (
              <div>
                <label className="label">Correo Zoom</label>
                <input className="input" value={form.zoom_email}
                  onChange={(e) => setForm({ ...form, zoom_email: e.target.value })}
                  placeholder={isGymScoped ? 'zoom@tugimnasio.com' : 'zoom@programa.com'} />
                {!isGymScoped ? zoomStatus.filter((z) => z.program_id === program.id).map((z) => (
                  <p key={z.account_id || z.email} className="text-xs mt-2 text-stone-500">
                    Estado API: {z.configured ? '✓ configurado' : '— sin credenciales env'}
                  </p>
                )) : null}
              </div>
            ) : (
              <div className="space-y-3">
                {form.zoom_accounts.map((acc, idx) => (
                  <div key={acc.id || idx} className="border border-slate-200 rounded-2xl p-4 space-y-2">
                    <input className="input" placeholder="Etiqueta" value={acc.label || ''}
                      onChange={(e) => updateZoomAccount(idx, 'label', e.target.value)} />
                    <input className="input" placeholder="Correo Zoom" value={acc.email || ''}
                      onChange={(e) => updateZoomAccount(idx, 'email', e.target.value)} />
                    <textarea className="input" rows={2} placeholder="Observaciones"
                      value={acc.observaciones || ''}
                      onChange={(e) => updateZoomAccount(idx, 'observaciones', e.target.value)} />
                    <label className="inline-flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={acc.activo !== false}
                        onChange={(e) => updateZoomAccount(idx, 'activo', e.target.checked)} />
                      Activo
                    </label>
                  </div>
                ))}
                <button type="button" className="btn-secondary text-sm" onClick={addZoomAccount}>+ Cuenta Zoom</button>
              </div>
            )}
            <button type="button" className="btn-primary" onClick={saveProgram}>Guardar Zoom</button>
          </div>
        )}

        {tab === 'planes' && (
          <div className="max-h-[70vh] overflow-y-auto pr-1">
            <AdminPlans
              embedded
              mode="d28d"
              programId={program.id}
              gymId={isGymScoped ? gymId : null}
              hideProgramColumn
              title={`Planes — ${program.name}`}
            />
          </div>
        )}
      </div>
    </div>
  );
}
